import { useState } from 'react';
import { useLang } from '@/hooks/useLang';

const BASE = 'https://www.yamamoto-ss.co.jp/sp_recycle';

const products = [
  {
    href: `${BASE}/products/melter/`,
    img: `${BASE}/_resources/img/products_lineup_melter.jpg`,
    bgImg: 'https://public.readdy.ai/ai/img_res/70af23727eda0641b253cdfe6fbfe4a5.jpg',
    labelEn: 'EPS Compactors / Densifiers',
    labelJa: '発泡スチロール減容機',
    descEn: 'High-throughput EPS foam reduction for North American recyclers',
    descJa: '北米リサイクル業者向け高処理能力発泡スチロール減容機',
    models: ['RE-E201', 'RE-E502', 'RE-E1000', 'RE-E2000'],
    accent: '#c0392b',
  },
  {
    href: `${BASE}/products/pet/`,
    img: `${BASE}/_resources/img/products_lineup_pet.jpg`,
    bgImg: 'https://readdy.ai/api/search-image?query=PET%20bottle%20recycling%20machine%20label%20remover%20industrial%20equipment%20clean%20white%20background%20professional%20product%20photography%20high%20contrast%20dramatic%20lighting&width=600&height=400&seq=prod2&orientation=landscape',
    labelEn: 'PET Bottle Recycling',
    labelJa: 'ペットボトルリサイクル機器',
    descEn: 'Automated label removal and pre-processing for PET streams',
    descJa: 'ペットボトルの自動ラベル分離・前処理機器',
    models: ['PLS-2000', 'PLS-5000', 'PBD-5000', 'MBS-3000'],
    accent: '#c0392b',
  },
  {
    href: `${BASE}/products/bag/`,
    img: `${BASE}/_resources/img/products_lineup_bag.jpg`,
    bgImg: 'https://readdy.ai/api/search-image?query=industrial%20bag%20breaker%20shredder%20machine%20recycling%20waste%20processing%20equipment%20clean%20white%20background%20professional%20product%20photography%20dramatic%20lighting&width=600&height=400&seq=prod3&orientation=landscape',
    labelEn: 'Bag Breakers',
    labelJa: '破袋機',
    descEn: 'Efficient bag opening for sorted waste collection streams',
    descJa: '分別収集袋の効率的な破袋処理',
    models: [],
    accent: '#c0392b',
  },
  {
    href: `${BASE}/products/transport/`,
    img: `${BASE}/_resources/img/products_lineup_transport.jpg`,
    bgImg: 'https://readdy.ai/api/search-image?query=vertical%20conveyor%20elevator%20industrial%20material%20handling%20equipment%20recycling%20facility%20clean%20white%20background%20professional%20product%20photography%20dramatic%20lighting&width=600&height=400&seq=prod4&orientation=landscape',
    labelEn: 'Vertical Conveyors',
    labelJa: '垂直搬送機',
    descEn: 'Space-saving vertical material transport for recycling lines',
    descJa: 'リサイクルラインのスペース効率的な垂直搬送',
    models: [],
    accent: '#c0392b',
  },
  {
    href: `${BASE}/products/grush/`,
    img: `${BASE}/_resources/img/products_lineup_grush.jpg`,
    bgImg: 'https://readdy.ai/api/search-image?query=plastic%20granulator%20pelletizer%20machine%20industrial%20recycling%20equipment%20clean%20white%20background%20professional%20product%20photography%20high%20contrast%20dramatic%20lighting&width=600&height=400&seq=prod5&orientation=landscape',
    labelEn: 'Granule Compactors',
    labelJa: '造粒減容機',
    descEn: 'Precision granulation for market-ready recycled material',
    descJa: '市場品質のリサイクル原料を生み出す精密造粒機',
    models: ['FG-501BW'],
    accent: '#c0392b',
  },
];

export default function ProductsSection() {
  const { lang, t } = useLang();
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-screen-xl mx-auto px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-px bg-[#c0392b]"></div>
              <span className="text-[#c0392b] text-xs font-bold uppercase tracking-[0.2em]">
                {t('Product Lineup', '製品ラインアップ')}
              </span>
            </div>
            <h2 className="text-4xl font-black text-[#0a0a0a] leading-tight">
              {t('Our Equipment', '製品一覧')}
            </h2>
          </div>
          <a
            href={`${BASE}/products/melter/`}
            className="group inline-flex items-center gap-2 text-sm font-bold text-[#0a0a0a] border-b-2 border-[#c0392b] pb-1 hover:text-[#c0392b] transition-colors whitespace-nowrap self-start md:self-auto"
          >
            {t('View All Products', 'すべての製品を見る')}
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </a>
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border border-[#e8e8e8]">
          {products.map((p, i) => (
            <a
              key={p.href}
              href={p.href}
              className="group relative overflow-hidden cursor-pointer border-r border-[#e8e8e8] last:border-r-0"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Image */}
              <div className="relative w-full h-52 overflow-hidden bg-[#f5f5f5]">
                <img
                  src={p.img}
                  alt={lang === 'en' ? p.labelEn : p.labelJa}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                {/* Dark overlay on hover */}
                <div className={`absolute inset-0 bg-[#0a0a0a] transition-opacity duration-500 ${hovered === i ? 'opacity-60' : 'opacity-0'}`}></div>
                {/* Red accent bar */}
                <div className={`absolute bottom-0 left-0 h-0.5 bg-[#c0392b] transition-all duration-500 ${hovered === i ? 'w-full' : 'w-0'}`}></div>
              </div>

              {/* Content */}
              <div className="p-5 bg-white">
                <p className="text-xs font-black text-[#0a0a0a] leading-snug mb-2 group-hover:text-[#c0392b] transition-colors">
                  {lang === 'en' ? p.labelEn : p.labelJa}
                </p>
                <p className="text-[10px] text-[#888] leading-snug mb-3">
                  {lang === 'en' ? p.descEn : p.descJa}
                </p>
                {p.models.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {p.models.map(m => (
                      <span key={m} className="text-[9px] bg-[#f0f0f0] text-[#666] px-1.5 py-0.5 font-mono">{m}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Arrow indicator */}
              <div className={`absolute top-4 right-4 w-7 h-7 flex items-center justify-center bg-[#c0392b] text-white transition-all duration-300 ${hovered === i ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
                <i className="ri-arrow-right-up-line text-xs"></i>
              </div>
            </a>
          ))}
        </div>

        {/* Purasiru special card */}
        <div className="mt-4">
          <a
            href={`${BASE}/products/placil/`}
            className="group flex flex-col md:flex-row items-stretch overflow-hidden border border-[#e8e8e8] hover:border-[#c0392b] transition-all duration-300 cursor-pointer"
          >
            <div className="relative w-full md:w-72 h-48 overflow-hidden bg-[#0a0a0a] flex-shrink-0">
              <img
                src={`${BASE}/_resources/img/products_lineup_placil.jpg`}
                alt="Purasiru"
                className="w-full h-full object-cover object-top opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white text-3xl font-black tracking-widest opacity-20 group-hover:opacity-40 transition-opacity">ぷらしる</span>
              </div>
            </div>
            <div className="flex-1 px-8 py-6 flex flex-col justify-center bg-white">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-px bg-[#c0392b]"></div>
                <span className="text-[10px] text-[#c0392b] font-bold uppercase tracking-widest">{t('Plastic ID Technology', 'プラスチック材質判別')}</span>
              </div>
              <h3 className="text-xl font-black text-[#0a0a0a] mb-2 group-hover:text-[#c0392b] transition-colors">
                {t('"Purasiru" Plastic Material ID Device', 'プラスチック材質判別装置 ぷらしる')}
              </h3>
              <p className="text-sm text-[#666] leading-relaxed mb-4">
                {t(
                  'Handheld plastic material identification — easy to use, portable, and accurate for anyone on the floor. Instantly identify plastic types to maximize recycling value.',
                  '誰でも、簡単に、持ち運べてプラスチック材質を判別するハンディタイプの製品です。'
                )}
              </p>
              <div className="flex items-center gap-2 text-sm font-bold text-[#c0392b]">
                {t('Learn More', '詳細を見る')}
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
